import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { DEFAULT_VOXEL_SIZE_FT, EDIT_ACTIVE_COLOR, INTERSECTION_COLOR, REPLACEMENT_CLEARANCE_FT, TRAILER_BOX_NAME } from '../editMode.constants';
import {
  IDENTITY_MATRIX,
  LayerDataStructure,
  RenestNestedPart,
  buildPartialRenestPayload,
  flattenPlacableNestedParts,
  isSupportPartId,
} from '../partialRenest.utils';
import { clampPivotToBounds, computeEngineOrientedPlacementMatrix, computeInPlaneOrientationMatrix, computeNextOrientationIndex, computePositionsZLevel, computeSlotPlacementMatrix, extractOrientationAngleDeg, fitPlacementToTruckBounds, fromMatrix4, getFloorDepth, getOrientationStepConfig, normalizeAngleDeg } from '../editMode.utils';
import { PartOrientation, PartOrientationsItem } from '../../../../contexts/LoadingPlanContext';
import { CollisionContext, createCollisionContext, findIntersectingParts, hasIntersection, isEffectivelyVisible } from '../collision.utils';
import { SelectedReplacementPart } from '../ReplacePartModal/ReplacePartModal';
import { getIdlePartColor } from './usePartLoading';
import { enqueueSnackbar } from 'notistack';
import loadingPlansService from '../../../../services/api/loadingPlansService';
import {
  PlacementGrid,
  createPlacementGrid,
  isMatrixOnValidCell,
  isOrientationUnfitError,
  snapMatrixToGridCell,
} from '../partPositions.utils';

// Tolerance when deciding that a part is still at the Z the grid positions were requested for.
const GRID_Z_TOLERANCE_FT = 1e-4;

// Precise: a part whose local frame is rotated (e.g. a yawed beam) has a much looser AABB than its real extent.
const measurePartBox = (part: THREE.Object3D) => new THREE.Box3().setFromObject(part, true);

interface UseEditModeParams {
  viewer: any;
  currentLoadingPlan: any;
  scenePartsMap: { [partId: string]: THREE.Object3D };
  setScenePartsMap: React.Dispatch<React.SetStateAction<{ [partId: string]: THREE.Object3D }>>;
  palletChildrenMap: { [palletId: string]: string[] };
  getPartFromScene: (partId: string) => THREE.Object3D | null;
  loadSinglePart: (part: any) => Promise<THREE.Object3D | null>;
  currentPartRef: React.MutableRefObject<THREE.Object3D | null>;
  setActivePartId: (partId: string | null) => void;
  replacementAcceptedRef: React.MutableRefObject<boolean>;
  getTransformControls: () => any;
  getTruckBoundsForEdit: () => THREE.Box3 | null;
  setPartColor: (part: THREE.Object3D, color: number) => void;
  isReplaceModalOpen: boolean;
  // The engine's candidate orientations (not part of the plan payload); null when not loaded.
  getPartsOrientations: () => PartOrientationsItem[] | null;
  // The loaded layers and the layer of the current step: they decide which parts precede the edited one.
  layerData: { [key: number]: LayerDataStructure };
  currentLayer: number;
}

export function useEditMode({
  viewer,
  currentLoadingPlan,
  scenePartsMap,
  setScenePartsMap,
  palletChildrenMap,
  getPartFromScene,
  loadSinglePart,
  currentPartRef,
  setActivePartId,
  replacementAcceptedRef,
  getTransformControls,
  getTruckBoundsForEdit,
  setPartColor,
  isReplaceModalOpen,
  getPartsOrientations,
  layerData,
  currentLayer,
}: UseEditModeParams) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isPartIntersecting, setIsPartIntersecting] = useState(false);
  const [isOrientationUnfit, setIsOrientationUnfit] = useState(false);
  // A positions request for the current pose is in flight; until it answers only the mesh test stands.
  const [isLoadingPartPositions, setIsLoadingPartPositions] = useState(false);
  const [editOrientationAngle, setEditOrientationAngle] = useState<number>(0);
  const [editOrientationStep, setEditOrientationStep] = useState<number>(0);
  const [editOrientationTotal, setEditOrientationTotal] = useState<number>(0);
  const [hasEngineOrientations, setHasEngineOrientations] = useState<boolean>(false);

  const editRotationStepRef = useRef<number>(0);
  const editBaseAngleRef = useRef<number>(0);
  const editStepCountRef = useRef<number>(2);
  const editStepAngleDegRef = useRef<number>(180);
  const editBaseOrientationIndexRef = useRef<number | undefined>(undefined);
  const editCurrentOrientationIndexRef = useRef<number | undefined>(undefined);
  const editAvailableOrientationsRef = useRef<PartOrientation[] | null>(null);
  const editBaseFloorZRef = useRef<number | null>(null);
  const editBasePositionRef = useRef<THREE.Vector3 | null>(null);
  // Unrotated base transform used by the orientation-step cycle (distinct from
  // editOriginalMatrixRef, which is only for restore-on-cancel, and editDragBaseMatrixRef, which
  // tracks the post-drag reference frame).
  const editRotationBaseMatrixRef = useRef<THREE.Matrix4 | null>(null);
  const editClampedDeltaRef = useRef<THREE.Vector3 | null>(null);
  const editChildrenRelativeMatricesRef = useRef<Map<string, THREE.Matrix4> | null>(null);
  const editChildrenOriginalMatricesRef = useRef<Map<string, THREE.Matrix4> | null>(null);
  const editDragEndListenerRef = useRef<((event: any) => void) | null>(null);
  const orientationAnimFrameRef = useRef<number | null>(null);
  const isOrientationAnimatingRef = useRef<boolean>(false);
  const targetOrientationStepRef = useRef<number | null>(null);

  // Transform being edited (restore-on-cancel reference lives in editOriginalMatrixRef).
  const editPartRef = useRef<THREE.Object3D | null>(null);
  const editOriginalMatrixRef = useRef<THREE.Matrix4 | null>(null);
  // Updated after each drag-end; the reference frame subsequent drags/rotations build on.
  const editDragBaseMatrixRef = useRef<THREE.Matrix4 | null>(null);
  const editPivotRef = useRef<THREE.Object3D | null>(null);
  const editPivotStartRef = useRef<THREE.Vector3 | null>(null);
  const editDragListenerRef = useRef<(() => void) | null>(null);
  const editTruckBoundsRef = useRef<THREE.Box3 | null>(null);
  const editBoxMinOffsetRef = useRef<THREE.Vector3 | null>(null);
  const editBoxMaxOffsetRef = useRef<THREE.Vector3 | null>(null);
  const editIsIntersectingRef = useRef(false);
  const editBaselineContactsRef = useRef<Set<string>>(new Set());
  const editCollisionContextRef = useRef<CollisionContext | null>(null);
  // Replacements only: the engine rejects a fixed part that touches another part, so overlaps are checked with zero clearance.
  const editStrictContextRef = useRef<CollisionContext | null>(null);
  const editStrictBaselineRef = useRef<Set<string>>(new Set());
  const replacedSlotInfoRef = useRef<{
    oldPart: THREE.Object3D;
    oldPartId: string;
    oldPartMatrix: THREE.Matrix4;
    replacementPartId: string;
    replacementWasPlaced: boolean;
    replacementOriginalMatrix: THREE.Matrix4;
    replacementOriginalVisible: boolean;
  } | null>(null);

  const editPartIndexRef = useRef<number | undefined>(undefined);
  const editGridRef = useRef<PlacementGrid | null>(null);
  // Part Z the latest positions request was made for; the cells are only valid at that Z.
  const editGridMatrixZRef = useRef<number>(0);
  // Engine voxel size from the latest answer. Unlike editGridRef it survives a discard, so the next request's
  // z-level (which depends on it) and orientation snapping can still use it.
  const editCellSizeRef = useRef<number>(DEFAULT_VOXEL_SIZE_FT);
  // The engine answered that the part cannot be supported at the requested Z in this orientation (no cell is valid).
  const editOrientationUnfitRef = useRef(false);
  // Incremented per request and on discard so late responses are ignored.
  const positionsRequestIdRef = useRef(0);
  const editIsDraggingRef = useRef(false);

  const isPartOverlapping = useCallback((part: THREE.Object3D, context: CollisionContext) => {
    const strictContext = editStrictContextRef.current;
    return (
      hasIntersection(part, context, editBaselineContactsRef.current) ||
      (strictContext !== null && hasIntersection(part, strictContext, editStrictBaselineRef.current))
    );
  }, []);

  const updateChildObjects = useCallback((parentPart: THREE.Object3D) => {
    const relMap = editChildrenRelativeMatricesRef.current;
    if (!relMap || relMap.size === 0) return;

    relMap.forEach((relMatrix, childUuid) => {
      const childObj = scenePartsMap[childUuid];
      if (childObj) {
        childObj.matrix.copy(parentPart.matrix).multiply(relMatrix);
        childObj.matrixWorldNeedsUpdate = true;
        childObj.updateMatrixWorld(true);
      }
    });
  }, [scenePartsMap]);

  // The engine's answers describe one Z only, so they apply only while the part is still at that Z.
  const isAtRequestedZ = useCallback(
    (part: THREE.Object3D) => Math.abs(part.matrix.elements[14] - editGridMatrixZRef.current) < GRID_Z_TOLERANCE_FT,
    []
  );

  const getUsableGrid = useCallback((part: THREE.Object3D): PlacementGrid | null => {
    const grid = editGridRef.current;
    return grid && isAtRequestedZ(part) ? grid : null;
  }, [isAtRequestedZ]);

  // Puts the part origin exactly on the corner of the grid cell it is over; Z is never touched.
  const snapPartToGridCell = useCallback((part: THREE.Object3D): boolean => {
    const grid = getUsableGrid(part);
    if (!grid) return false;
    part.matrix.copy(
      snapMatrixToGridCell(part.matrix, grid.baseOrientationTransform, grid.gridCellSize).matrix
    );
    part.matrixWorldNeedsUpdate = true;
    part.updateMatrixWorld(true);
    updateChildObjects(part);
    return true;
  }, [getUsableGrid, updateChildObjects]);

  // Re-bases the pivot and the drag/rotation references on wherever the part now is.
  const syncEditBaseToPart = useCallback(() => {
    const part = editPartRef.current;
    const pivot = editPivotRef.current;
    const start = editPivotStartRef.current;
    if (!part || !pivot || !start) return;

    const box = measurePartBox(part);
    if (box.isEmpty()) return;
    const center = box.getCenter(new THREE.Vector3());

    const delta = center.clone().sub(start);
    if (delta.lengthSq() > 1e-6) {
      editBasePositionRef.current?.add(delta);
      if (editBaseFloorZRef.current !== null) {
        editBaseFloorZRef.current += delta.z;
      }
      const parentWorld = part.parent?.matrixWorld ?? new THREE.Matrix4();
      const moveMatrix = new THREE.Matrix4().makeTranslation(delta.x, delta.y, delta.z);
      const localMove = parentWorld.clone().invert().multiply(moveMatrix).multiply(parentWorld);
      editRotationBaseMatrixRef.current?.premultiply(localMove);
    }

    pivot.position.copy(center);
    pivot.updateMatrixWorld(true);
    editDragBaseMatrixRef.current = part.matrix.clone();
    editPivotStartRef.current = center;
  }, []);

  // Single place that decides the part colour / invalid flag. The engine's answer for the current pose is the
  // verdict once loaded - its cell, or "cannot be supported at this level in this orientation" - and until then
  // (and after a vertical move, when the old answer no longer applies) the mesh test stands in.
  const updatePlacementValidity = useCallback(() => {
    const part = editPartRef.current;
    if (!part) return;

    const isUnfit = editOrientationUnfitRef.current && isAtRequestedZ(part);
    const grid = getUsableGrid(part);
    const context = editCollisionContextRef.current;
    const invalid =
      isUnfit ||
      (grid ? !isMatrixOnValidCell(part.matrix, grid) : context !== null && isPartOverlapping(part, context));

    setPartColor(part, invalid ? INTERSECTION_COLOR : EDIT_ACTIVE_COLOR);
    editIsIntersectingRef.current = invalid;
    setIsPartIntersecting(invalid);
    setIsOrientationUnfit(isUnfit);
  }, [isAtRequestedZ, getUsableGrid, isPartOverlapping, setPartColor]);

  // Drops the engine's answer and turns any request still in flight into a stale one.
  const discardPartPositions = useCallback(() => {
    positionsRequestIdRef.current++;
    editGridRef.current = null;
    editOrientationUnfitRef.current = false;
    setIsLoadingPartPositions(false);
  }, []);

  const loadPartPositions = useCallback(
    async (partIndex: number, orientationIndex: number) => {
      const part = editPartRef.current;
      discardPartPositions();
      const requestId = positionsRequestIdRef.current;

      if (!part || !currentLoadingPlan?.id || isSupportPartId(part.uuid)) {
        return;
      }
      editGridMatrixZRef.current = part.matrix.elements[14];

      // Ask for the layer plane the part actually rests on, so the cells describe its own level. Without a
      // z-level the engine searches the top levels instead.
      part.updateMatrixWorld(true);
      const box = measurePartBox(part);
      const zLevel = box.isEmpty()
        ? undefined
        : computePositionsZLevel(
            box.min.z,
            editAvailableOrientationsRef.current?.[orientationIndex],
            editCellSizeRef.current
          );

      // The obstacles are what accepting this edit keeps (the same set the re-nest locks): the parts loaded before
      // it - earlier layers, earlier in its layer, stacked on pallets - and the dunnage. Parts loaded after it are
      // re-nested on Accept, so they must not count; the engine would also judge this part against parts that were
      // not there when it was placed, and its own spot then fails the engine's accessibility check. Dunnage never
      // blocks, but the engine needs a kept item to set up its layer state when nothing precedes the part.
      // The part being replaced is hidden but still listed; it must not block its own slot.
      const replacedOldPartId = replacedSlotInfoRef.current?.oldPartId;
      const rawNestedParts = currentLoadingPlan?.nestingOutput?.nested_parts || [];
      const keptParts = buildPartialRenestPayload({
        rawNestedParts,
        layerData,
        currentLayer,
        editedPartId: part.uuid,
        newTransformMatrix: fromMatrix4(part.matrix),
        replacedOldPartId,
        scenePartsMap,
      });
      // Not found in its layer: Accept then records the edit in place and keeps every other part.
      const obstacles: RenestNestedPart[] = keptParts.length > 0
        ? keptParts
        : flattenPlacableNestedParts(rawNestedParts.filter((p: any) => p.part_id !== part.uuid && p.part_id !== replacedOldPartId));
      const otherNestedParts = obstacles
        .filter((p) => p.part_id !== part.uuid && p.part_id !== replacedOldPartId)
        .map((p) => ({
          part_id: p.part_id,
          part_index: p.part_index,
          orientation_index: p.orientation_index,
          transform_matrix: p.transform_matrix ?? IDENTITY_MATRIX,
        }));

      setIsLoadingPartPositions(true);
      try {
        const res = await loadingPlansService.fetchPartPositions(currentLoadingPlan.id, {
          partIndex,
          orientationIndex,
          ...(zLevel !== undefined ? { zLevel } : {}),
          nestedParts: otherNestedParts,
        });
        if (requestId !== positionsRequestIdRef.current) return;

        const level = res?.zLevels?.[0];
        if (level) {
          editGridRef.current = createPlacementGrid(level);
          if (Number.isFinite(level.gridCellSize) && level.gridCellSize > 0) {
            editCellSizeRef.current = level.gridCellSize;
          }
          // A drag in progress snaps on its own next frame; re-basing mid-drag would corrupt its delta.
          if (!editIsDraggingRef.current && snapPartToGridCell(part)) {
            syncEditBaseToPart();
          }
        }
      } catch (err) {
        if (requestId !== positionsRequestIdRef.current) return;
        if (isOrientationUnfitError(err)) {
          // A verdict, not a failure: in this orientation the part cannot be supported anywhere at this level.
          editOrientationUnfitRef.current = true;
        } else {
          console.warn('Failed to fetch part positions from API, validating with the mesh test only:', err);
        }
      }

      // Only the current request gets here (stale ones return above), so it owns the flag.
      setIsLoadingPartPositions(false);
      updatePlacementValidity();
      if (viewer) {
        (viewer as any).renderer?.render(viewer.getScene(), (viewer as any).camera);
      }
    },
    [
      currentLoadingPlan?.id,
      currentLoadingPlan?.nestingOutput?.nested_parts,
      layerData,
      currentLayer,
      scenePartsMap,
      discardPartPositions,
      snapPartToGridCell,
      syncEditBaseToPart,
      updatePlacementValidity,
      viewer,
    ]
  );

  // Orientation or Z changed: the previous cells no longer apply.
  const reloadPartPositions = useCallback((orientationIndex: number | undefined) => {
    if (editPartIndexRef.current !== undefined && orientationIndex !== undefined) {
      void loadPartPositions(editPartIndexRef.current, orientationIndex);
      return;
    }
    discardPartPositions();
  }, [loadPartPositions, discardPartPositions]);

  const applyOrientationStep = useCallback((nextStep: number) => {
    const part = editPartRef.current;
    const pivot = editPivotRef.current;
    if (!part || !pivot || !viewer) return;

    const basePosition = editBasePositionRef.current;
    const baseMatrix = editRotationBaseMatrixRef.current;
    if (!basePosition || !baseMatrix) return;

    editRotationStepRef.current = nextStep;

    const parentWorld = part.parent?.matrixWorld ?? new THREE.Matrix4();
    const invParentWorld = parentWorld.clone().invert();

    const availableOrientations = editAvailableOrientationsRef.current;
    if (availableOrientations && availableOrientations.length > 0) {
      const targetStep =
        ((nextStep % availableOrientations.length) + availableOrientations.length) %
        availableOrientations.length;
      const targetOri = availableOrientations[targetStep];
      // The support plane stays on the dunnage; this orientation's lowest vertex sits floor-depth below it.
      const floorZ = (editBaseFloorZRef.current ?? basePosition.z) - getFloorDepth(targetOri);

      const orientedMatrix = computeEngineOrientedPlacementMatrix(
        targetOri,
        basePosition,
        floorZ,
        invParentWorld,
        part,
        editCellSizeRef.current
      );

      part.matrix.copy(orientedMatrix);
      part.matrixWorldNeedsUpdate = true;
      part.updateMatrixWorld(true);

      const box = measurePartBox(part);
      const actualCenter = box.isEmpty() ? basePosition.clone() : box.getCenter(new THREE.Vector3());

      pivot.position.copy(actualCenter);
      const currentAngle = extractOrientationAngleDeg(part.matrix);
      pivot.rotation.z = THREE.MathUtils.degToRad(currentAngle);
      pivot.updateMatrixWorld(true);

      setEditOrientationAngle(currentAngle);
      setEditOrientationStep(targetStep);
      editCurrentOrientationIndexRef.current = targetStep;

      let minOffset = new THREE.Vector3();
      let maxOffset = new THREE.Vector3();
      if (!box.isEmpty()) {
        minOffset = box.min.clone().sub(actualCenter);
        maxOffset = box.max.clone().sub(actualCenter);
        editBoxMinOffsetRef.current = minOffset.clone();
        editBoxMaxOffsetRef.current = maxOffset.clone();
      }

      const truckBounds = editTruckBoundsRef.current;
      if (truckBounds && !box.isEmpty()) {
        const clampedPosition = clampPivotToBounds(
          actualCenter,
          minOffset,
          maxOffset,
          truckBounds
        );
        // Only clamp horizontally (X, Y) so in-place rotation never lifts parts into the ceiling:
        clampedPosition.z = actualCenter.z;
        const clampDelta = clampedPosition.clone().sub(actualCenter);
        if (clampDelta.lengthSq() > 1e-6) {
          pivot.position.copy(clampedPosition);
          const worldMove = new THREE.Matrix4().makeTranslation(
            clampDelta.x,
            clampDelta.y,
            0
          );
          const localMove = invParentWorld.clone().multiply(worldMove).multiply(parentWorld);
          part.matrix.copy(localMove.multiply(orientedMatrix));
          part.matrixWorldNeedsUpdate = true;
          part.updateMatrixWorld(true);
        }
      }

      editClampedDeltaRef.current = pivot.position.clone().sub(basePosition);
      updateChildObjects(part);

      editDragBaseMatrixRef.current = part.matrix.clone();
      editPivotStartRef.current = pivot.position.clone();

      reloadPartPositions(targetStep);
      updatePlacementValidity();

      const scene = viewer.getScene();
      const camera = (viewer as any).camera;
      (viewer as any).renderer.render(scene, camera);
      return;
    }

    if (nextStep === 0) {
      part.matrix.copy(baseMatrix);
      pivot.position.copy(basePosition);
      pivot.rotation.z = THREE.MathUtils.degToRad(editBaseAngleRef.current);
      editClampedDeltaRef.current = new THREE.Vector3(0, 0, 0);

      part.matrixWorldNeedsUpdate = true;
      part.updateMatrixWorld(true);
      pivot.updateMatrixWorld(true);

      const box = measurePartBox(part);
      if (!box.isEmpty()) {
        editBoxMinOffsetRef.current = box.min.clone().sub(pivot.position);
        editBoxMaxOffsetRef.current = box.max.clone().sub(pivot.position);
      }
      setEditOrientationAngle(editBaseAngleRef.current);
      editCurrentOrientationIndexRef.current = editBaseOrientationIndexRef.current;
    } else {
      const stepAngleDeg = editStepAngleDegRef.current;
      const deltaAngle = nextStep * (stepAngleDeg * (Math.PI / 180));
      const rotatedMatrix = computeInPlaneOrientationMatrix(
        baseMatrix,
        basePosition,
        deltaAngle,
        invParentWorld,
        parentWorld,
      );

      part.matrix.copy(rotatedMatrix);
      part.matrixWorldNeedsUpdate = true;
      part.updateMatrixWorld(true);

      const box = measurePartBox(part);
      const actualCenter = box.isEmpty() ? basePosition.clone() : box.getCenter(new THREE.Vector3());

      const currentAngle = normalizeAngleDeg(editBaseAngleRef.current + nextStep * stepAngleDeg);
      pivot.position.copy(actualCenter);
      pivot.rotation.z = THREE.MathUtils.degToRad(currentAngle);
      pivot.updateMatrixWorld(true);

      setEditOrientationAngle(currentAngle);

      const nextOriIndex = computeNextOrientationIndex(
        editBaseOrientationIndexRef.current,
        nextStep,
        editStepCountRef.current === 4,
      );
      editCurrentOrientationIndexRef.current = nextOriIndex;

      let minOffset = new THREE.Vector3();
      let maxOffset = new THREE.Vector3();
      if (!box.isEmpty()) {
        minOffset = box.min.clone().sub(actualCenter);
        maxOffset = box.max.clone().sub(actualCenter);
      }

      const truckBounds = editTruckBoundsRef.current;
      if (truckBounds && !box.isEmpty()) {
        const clampedPosition = clampPivotToBounds(
          actualCenter,
          minOffset,
          maxOffset,
          truckBounds,
        );
        // Only clamp horizontally (X, Y) so in-place rotation never lifts parts into the ceiling:
        clampedPosition.z = actualCenter.z;

        if (!clampedPosition.equals(actualCenter)) {
          const deltaClamp = clampedPosition.clone().sub(actualCenter);
          pivot.position.copy(clampedPosition);
          pivot.updateMatrixWorld(true);

          const deltaMatrix = new THREE.Matrix4().makeTranslation(deltaClamp.x, deltaClamp.y, 0);
          const localDelta = invParentWorld.clone().multiply(deltaMatrix).multiply(parentWorld);
          part.matrix.premultiply(localDelta);
          part.matrixWorldNeedsUpdate = true;
          part.updateMatrixWorld(true);

          editClampedDeltaRef.current = deltaClamp;
        } else {
          editClampedDeltaRef.current = new THREE.Vector3(0, 0, 0);
        }
      } else {
        editClampedDeltaRef.current = new THREE.Vector3(0, 0, 0);
      }

      const finalBox = measurePartBox(part);
      if (!finalBox.isEmpty()) {
        editBoxMinOffsetRef.current = finalBox.min.clone().sub(pivot.position);
        editBoxMaxOffsetRef.current = finalBox.max.clone().sub(pivot.position);
      }
    }

    updateChildObjects(part);

    editDragBaseMatrixRef.current = part.matrix.clone();
    editPivotStartRef.current = pivot.position.clone();

    reloadPartPositions(editCurrentOrientationIndexRef.current);
    updatePlacementValidity();

    const scene = viewer.getScene();
    const camera = (viewer as any).camera;
    (viewer as any).renderer.render(scene, camera);
  }, [viewer, updateChildObjects, reloadPartPositions, updatePlacementValidity]);

  const cancelOrientationAnimation = useCallback((snapToTarget: boolean = true) => {
    if (orientationAnimFrameRef.current !== null) {
      cancelAnimationFrame(orientationAnimFrameRef.current);
      orientationAnimFrameRef.current = null;
    }
    if (isOrientationAnimatingRef.current) {
      isOrientationAnimatingRef.current = false;
      const target = targetOrientationStepRef.current;
      targetOrientationStepRef.current = null;
      if (snapToTarget && target !== null) {
        applyOrientationStep(target);
      }
    }
  }, [applyOrientationStep]);

  const animateOrientationStep = useCallback((targetStep: number, isForward: boolean) => {
    const part = editPartRef.current;
    const pivot = editPivotRef.current;
    const basePosition = editBasePositionRef.current;
    const baseMatrix = editRotationBaseMatrixRef.current;
    if (!part || !pivot || !viewer || !basePosition || !baseMatrix) {
      applyOrientationStep(targetStep);
      return;
    }

    if (isOrientationAnimatingRef.current) {
      cancelOrientationAnimation(true);
    }

    const currentStep = editRotationStepRef.current;
    if (currentStep === targetStep) return;

    if (editAvailableOrientationsRef.current && editAvailableOrientationsRef.current.length > 0) {
      applyOrientationStep(targetStep);
      return;
    }

    // The pose changes for the whole animation; an answer for the old orientation must not land mid-way.
    discardPartPositions();
    isOrientationAnimatingRef.current = true;
    targetOrientationStepRef.current = targetStep;

    const stepAngleDeg = editStepAngleDegRef.current;
    const startStepAngleRad = currentStep * (stepAngleDeg * (Math.PI / 180));
    const deltaAngleRad = isForward
      ? stepAngleDeg * (Math.PI / 180)
      : -stepAngleDeg * (Math.PI / 180);

    const parentWorld = part.parent?.matrixWorld ?? new THREE.Matrix4();
    const invParentWorld = parentWorld.clone().invert();

    const duration = 250; // ms
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1.0);
      const ease = 1 - Math.pow(1 - progress, 3); // cubic ease-out

      const currentStepAngleRad = startStepAngleRad + deltaAngleRad * ease;

      const currentInterpMatrix = computeInPlaneOrientationMatrix(
        baseMatrix,
        basePosition,
        currentStepAngleRad,
        invParentWorld,
        parentWorld,
      );

      part.matrix.copy(currentInterpMatrix);
      part.matrixWorldNeedsUpdate = true;
      part.updateMatrixWorld(true);
      updateChildObjects(part);

      const currentYaw = normalizeAngleDeg(
        editBaseAngleRef.current + (currentStepAngleRad * (180 / Math.PI))
      );
      const animBox = measurePartBox(part);
      if (!animBox.isEmpty()) {
        pivot.position.copy(animBox.getCenter(new THREE.Vector3()));
      }
      pivot.rotation.z = THREE.MathUtils.degToRad(currentYaw);
      pivot.updateMatrixWorld(true);

      setEditOrientationAngle(currentYaw);

      const scene = viewer.getScene();
      const camera = (viewer as any).camera;
      (viewer as any).renderer.render(scene, camera);

      if (progress < 1.0) {
        orientationAnimFrameRef.current = requestAnimationFrame(animate);
      } else {
        isOrientationAnimatingRef.current = false;
        orientationAnimFrameRef.current = null;
        targetOrientationStepRef.current = null;
        applyOrientationStep(targetStep);
      }
    };

    orientationAnimFrameRef.current = requestAnimationFrame(animate);
  }, [viewer, applyOrientationStep, cancelOrientationAnimation, updateChildObjects, discardPartPositions]);

  const handleOrientationNext = useCallback(() => {
    const nextStep = (editRotationStepRef.current + 1) % editStepCountRef.current;
    animateOrientationStep(nextStep, true);
  }, [animateOrientationStep]);

  const handleOrientationPrev = useCallback(() => {
    const prevStep =
      (editRotationStepRef.current + editStepCountRef.current - 1) % editStepCountRef.current;
    animateOrientationStep(prevStep, false);
  }, [animateOrientationStep]);

  // Read from the scene rather than scenePartsMap, which can still be stale during a replacement.
  // Parts not yet placed/revealed (future steps) are hidden via `visible = false`, not removed from
  // the scene, so they must be excluded here or they'd be treated as physical obstacles.
  const collectCollisionCandidates = useCallback((movingPart: THREE.Object3D) => {
    const candidates: THREE.Object3D[] = [];
    viewer?.getScene().traverse((child: THREE.Object3D) => {
      if (
        child.type === 'Object3D' &&
        child.name !== TRAILER_BOX_NAME &&
        child.name !== 'edit-pivot' &&
        child.name !== 'orientation-indicator' &&
        child !== movingPart &&
        isEffectivelyVisible(child)
      ) {
        candidates.push(child);
      }
    });
    return candidates;
  }, [viewer]);

  const handleEnterEditMode = useCallback(() => {
    const part = currentPartRef.current;
    if (!viewer || !part) return;

    const transformControls = getTransformControls();
    if (!transformControls) return;

    const scene = viewer.getScene();
    const camera = (viewer as any).camera;

    // The viewer creates TransformControls on startup but never adds it to the
    // scene. Add it now so the gizmo is rendered.
    if (!scene.children.includes(transformControls)) {
      scene.add(transformControls);
    }

    // Save the part's current transform so Decline can restore it exactly.
    part.updateMatrixWorld(true);
    const originalMatrix = part.matrix.clone();
    editOriginalMatrixRef.current = originalMatrix;
    editDragBaseMatrixRef.current = originalMatrix.clone();
    editPartRef.current = part;

    // Create a proxy pivot at the part's visual center. The STL origin is far
    // from the visible geometry, so dragging the pivot is much more intuitive.
    const box = measurePartBox(part);
    const center = box.isEmpty()
      ? new THREE.Vector3().setFromMatrixPosition(part.matrixWorld)
      : box.getCenter(new THREE.Vector3());

    const pivot = new THREE.Object3D();
    pivot.name = 'edit-pivot';
    pivot.position.copy(center);
    // @ts-ignore
    scene.add(pivot);
    pivot.updateMatrixWorld(true);

    editPivotRef.current = pivot;
    editPivotStartRef.current = center.clone();
    editTruckBoundsRef.current = getTruckBoundsForEdit();
    editBoxMinOffsetRef.current = box.isEmpty() ? new THREE.Vector3() : box.min.clone().sub(center);
    editBoxMaxOffsetRef.current = box.isEmpty() ? new THREE.Vector3() : box.max.clone().sub(center);

    // Initialize deterministic rotation cycle state from clean base
    editRotationStepRef.current = 0;
    const baseAngle = extractOrientationAngleDeg(originalMatrix);
    editBaseAngleRef.current = baseAngle;
    setEditOrientationAngle(baseAngle);

    pivot.rotation.set(0, 0, THREE.MathUtils.degToRad(baseAngle));
    pivot.updateMatrixWorld(true);

    const size = box.isEmpty() ? new THREE.Vector3(1, 1, 1) : box.getSize(new THREE.Vector3());
    const stepConfig = getOrientationStepConfig(size.x, size.y);
    editStepCountRef.current = stepConfig.stepCount;
    editStepAngleDegRef.current = stepConfig.stepAngleDeg;

    const findNestedPart = (partId: string) => {
      const nested = currentLoadingPlan?.nestingOutput?.nested_parts || [];
      for (const p of nested) {
        if (p.part_id === partId) return p;
        if (p.child_items) {
          const child = p.child_items.find((c: any) => c.part_id === partId);
          if (child) return child;
        }
      }
      return null;
    };
    const rawNestedPart = findNestedPart(part.uuid);
    const initialOriIndex = rawNestedPart?.orientation_index;

    // Engine-computed orientations; without them (not loaded, or no full nesting run yet) rotation is in-plane only.
    const partOrientationsEntry = getPartsOrientations()?.find(
      (po) =>
        po.part_id === part.uuid ||
        (rawNestedPart?.part_index !== undefined && po.part_index === rawNestedPart.part_index)
    );
    const availableOrientations =
      partOrientationsEntry?.orientations && partOrientationsEntry.orientations.length > 0
        ? partOrientationsEntry.orientations
        : null;

    editAvailableOrientationsRef.current = availableOrientations;
    const lowestZ = box.isEmpty() ? center.z - 0.5 : box.min.z;
    editBaseFloorZRef.current = lowestZ;

    if (availableOrientations && availableOrientations.length > 0) {
      const initialStep = Math.max(
        0,
        Math.min(initialOriIndex ?? 0, availableOrientations.length - 1)
      );
      editBaseFloorZRef.current = lowestZ + getFloorDepth(availableOrientations[initialStep]);
      editRotationStepRef.current = initialStep;
      editStepCountRef.current = availableOrientations.length;
      editBaseOrientationIndexRef.current = initialStep;
      editCurrentOrientationIndexRef.current = initialStep;
      setEditOrientationStep(initialStep);
      setEditOrientationTotal(availableOrientations.length);
      setHasEngineOrientations(true);
    } else {
      editRotationStepRef.current = 0;
      editStepCountRef.current = stepConfig.stepCount;
      editBaseOrientationIndexRef.current = initialOriIndex;
      editCurrentOrientationIndexRef.current = initialOriIndex;
      setEditOrientationStep(0);
      setEditOrientationTotal(stepConfig.stepCount);
      setHasEngineOrientations(false);
    }

    // Without the engine part_index there is no way to ask for positions (falls back to the mesh test).
    const resolvedPartIndex = rawNestedPart?.part_index ?? partOrientationsEntry?.part_index;

    const activeOriIndex = editCurrentOrientationIndexRef.current ?? initialOriIndex ?? 0;
    editPartIndexRef.current = resolvedPartIndex;
    reloadPartPositions(activeOriIndex);

    editBasePositionRef.current = center.clone();
    editRotationBaseMatrixRef.current = originalMatrix.clone();
    editClampedDeltaRef.current = new THREE.Vector3(0, 0, 0);

    // If this part is a pallet with children, store children original and relative transforms
    const childUuids = palletChildrenMap[part.uuid] || [];
    const childrenRelMap = new Map<string, THREE.Matrix4>();
    const childrenOrigMap = new Map<string, THREE.Matrix4>();
    if (childUuids.length > 0) {
      const invParentMatrix = originalMatrix.clone().invert();
      childUuids.forEach((childUuid) => {
        const childObj = scenePartsMap[childUuid];
        if (childObj) {
          childrenOrigMap.set(childUuid, childObj.matrix.clone());
          childrenRelMap.set(childUuid, invParentMatrix.clone().multiply(childObj.matrix));
        }
      });
    }
    editChildrenRelativeMatricesRef.current = childrenRelMap;
    editChildrenOriginalMatricesRef.current = childrenOrigMap;

    // Only the edited part moves, so the other parts' bounds are prepared once here rather than
    // being recomputed on every drag event.
    const collisionContext = createCollisionContext(part, collectCollisionCandidates(part));
    editCollisionContextRef.current = collisionContext;

    // Contacts that already exist in the nested layout (neighbours, dunnage the part rests on) are
    // inherent to the plan, so they must not be reported as user-created overlaps.
    editBaselineContactsRef.current = new Set(
      findIntersectingParts(part, collisionContext).map((obj) => obj.uuid)
    );
    editIsIntersectingRef.current = false;
    setIsPartIntersecting(false);
    setPartColor(part, EDIT_ACTIVE_COLOR);

    // Move the real part by the same amount the pivot moves.
    const deltaMatrix = new THREE.Matrix4();
    const onDrag = () => {
      if (isOrientationAnimatingRef.current) {
        cancelOrientationAnimation(true);
      }
      const start = editPivotStartRef.current;
      if (!start) return;

      const truckBounds = editTruckBoundsRef.current;
      const minOffset = editBoxMinOffsetRef.current;
      const maxOffset = editBoxMaxOffsetRef.current;
      if (truckBounds && minOffset && maxOffset) {
        pivot.position.copy(clampPivotToBounds(pivot.position, minOffset, maxOffset, truckBounds));
      }

      deltaMatrix.makeTranslation(
        pivot.position.x - start.x,
        pivot.position.y - start.y,
        pivot.position.z - start.z,
      );
      part.matrix.copy(deltaMatrix).multiply(editDragBaseMatrixRef.current || originalMatrix);
      part.matrixWorldNeedsUpdate = true;
      part.updateMatrixWorld(true);
      updateChildObjects(part);

      // Show the part on the corner of the cell it is over, then judge exactly that cell.
      snapPartToGridCell(part);
      updatePlacementValidity();
    };

    transformControls.addEventListener('objectChange', onDrag);
    editDragListenerRef.current = onDrag;

    const onDraggingChanged = (event: any) => {
      editIsDraggingRef.current = Boolean(event.value);
      if (event.value) return;

      syncEditBaseToPart();

      // The cells only describe the Z they were requested for; after a vertical move ask for the new level.
      const currentZ = part.matrix.elements[14];
      if (
        editPartIndexRef.current !== undefined &&
        Math.abs(currentZ - editGridMatrixZRef.current) >= GRID_Z_TOLERANCE_FT
      ) {
        reloadPartPositions(editCurrentOrientationIndexRef.current);
      }
      updatePlacementValidity();
      (viewer as any).renderer.render(viewer.getScene(), camera);
    };

    transformControls.addEventListener('dragging-changed', onDraggingChanged);
    editDragEndListenerRef.current = onDraggingChanged;

    // TransformControls moves objects returned by getSelectedObjects(). The
    // viewer hardcodes this to an empty list, so aim it at our pivot instead.
    transformControls.getSelectedObjects = () => (editPivotRef.current ? [editPivotRef.current] : []);

    transformControls.camera = camera;
    transformControls.size = 1;
    transformControls.showX = true;
    transformControls.showY = true;
    transformControls.showZ = true;
    transformControls.setSpace('world');
    transformControls.setMode('translate');
    transformControls.setTranslationSnap(DEFAULT_VOXEL_SIZE_FT);
    transformControls.enabled = true;
    transformControls.visible = true;
    transformControls.attach(pivot);

    // Render once so the gizmo appears immediately.
    (viewer as any).renderer.render(scene, camera);
    setIsEditMode(true);
  }, [
    viewer,
    getTransformControls,
    getTruckBoundsForEdit,
    collectCollisionCandidates,
    scenePartsMap,
    palletChildrenMap,
    setPartColor,
    updateChildObjects,
    cancelOrientationAnimation,
    currentLoadingPlan?.nestingOutput?.nested_parts,
    getPartsOrientations,
    currentPartRef,
    reloadPartPositions,
    snapPartToGridCell,
    syncEditBaseToPart,
    updatePlacementValidity,
  ]);

  const exitEditMode = useCallback((restoreOriginal: boolean) => {
    const transformControls = getTransformControls();
    const part = editPartRef.current;

    if (transformControls) {
      if (editDragListenerRef.current) {
        transformControls.removeEventListener('objectChange', editDragListenerRef.current);
      }
      if (editDragEndListenerRef.current) {
        transformControls.removeEventListener('dragging-changed', editDragEndListenerRef.current);
      }
      transformControls.getSelectedObjects = () => [];
      transformControls.setTranslationSnap(null);
      transformControls.detach();
    }
    editDragListenerRef.current = null;
    editDragEndListenerRef.current = null;

    if (orientationAnimFrameRef.current !== null) {
      cancelAnimationFrame(orientationAnimFrameRef.current);
      orientationAnimFrameRef.current = null;
    }
    isOrientationAnimatingRef.current = false;
    targetOrientationStepRef.current = null;

    if (editPivotRef.current) {
      editPivotRef.current.parent?.remove(editPivotRef.current);
      editPivotRef.current = null;
    }
    editPivotStartRef.current = null;
    editTruckBoundsRef.current = null;
    editBoxMinOffsetRef.current = null;
    editBoxMaxOffsetRef.current = null;
    editIsIntersectingRef.current = false;
    editBaselineContactsRef.current = new Set();
    editCollisionContextRef.current = null;
    editStrictContextRef.current = null;
    editStrictBaselineRef.current = new Set();
    setIsPartIntersecting(false);
    setIsOrientationUnfit(false);

    editRotationStepRef.current = 0;
    editBaseAngleRef.current = 0;
    editStepCountRef.current = 2;
    editStepAngleDegRef.current = 180;
    editBaseOrientationIndexRef.current = undefined;
    editCurrentOrientationIndexRef.current = undefined;
    editAvailableOrientationsRef.current = null;
    editBaseFloorZRef.current = null;
    setEditOrientationAngle(0);
    setEditOrientationStep(0);
    setEditOrientationTotal(0);
    setHasEngineOrientations(false);
    editBasePositionRef.current = null;
    editRotationBaseMatrixRef.current = null;
    editClampedDeltaRef.current = null;
    editPartIndexRef.current = undefined;
    discardPartPositions();
    editIsDraggingRef.current = false;

    const replacedSlot = replacedSlotInfoRef.current;

    if (restoreOriginal) {
      if (editChildrenOriginalMatricesRef.current) {
        editChildrenOriginalMatricesRef.current.forEach((origMatrix, childUuid) => {
          const childObj = scenePartsMap[childUuid];
          if (childObj) {
            childObj.matrix.copy(origMatrix);
            childObj.matrixAutoUpdate = false;
            childObj.matrixWorldNeedsUpdate = true;
            childObj.updateMatrixWorld(true);
          }
        });
      }

      if (part) {
        if (editOriginalMatrixRef.current) {
          part.matrix.copy(editOriginalMatrixRef.current);
        }
        setPartColor(part, EDIT_ACTIVE_COLOR);
        part.matrixAutoUpdate = false;
        part.matrixWorldNeedsUpdate = true;
        part.updateMatrixWorld(true);
      }

      // Undo a pending replacement's geometry/visibility, but keep the viewer focused on the
      // part the user was just editing (the replacement) rather than jumping back to the part
      // it replaced - Cancel must never change which item is "current".
      if (replacedSlot && part) {
        if (replacedSlot.replacementWasPlaced) {
          part.matrix.copy(replacedSlot.replacementOriginalMatrix);
          part.visible = replacedSlot.replacementOriginalVisible;
        } else {
          part.visible = false;
        }
        part.matrixWorldNeedsUpdate = true;
        part.updateMatrixWorld(true);
        setPartColor(part, EDIT_ACTIVE_COLOR);

        const oldPart = replacedSlot.oldPart;
        oldPart.matrix.copy(replacedSlot.oldPartMatrix);
        oldPart.matrixAutoUpdate = false;
        oldPart.matrixWorldNeedsUpdate = true;
        oldPart.visible = true;
        setPartColor(oldPart, getIdlePartColor(replacedSlot.oldPartId));
        oldPart.updateMatrixWorld(true);
      }
    } else {
      if (part) {
        setPartColor(part, EDIT_ACTIVE_COLOR);
        part.matrixAutoUpdate = false;
        part.matrixWorldNeedsUpdate = true;
        part.updateMatrixWorld(true);
      }
    }

    replacedSlotInfoRef.current = null;
    editChildrenRelativeMatricesRef.current = null;
    editChildrenOriginalMatricesRef.current = null;
    editPartRef.current = null;
    editOriginalMatrixRef.current = null;
    editDragBaseMatrixRef.current = null;
    setIsEditMode(false);

    if (viewer) {
      const scene = viewer.getScene();
      (viewer as any).renderer.render(scene, (viewer as any).camera);
    }
  }, [viewer, getTransformControls, scenePartsMap, setPartColor, discardPartPositions]);

  const handleConfirmReplace = useCallback(
    async (replacement: SelectedReplacementPart) => {
      if (!viewer) return;

      const oldPart = currentPartRef.current;
      if (!oldPart) {
        enqueueSnackbar('No part is currently selected to replace.', { variant: 'error' });
        return;
      }

      // Snapshot the slot's world bounds before leaving edit mode: the replacement is dropped into
      // this volume. The slot's transform_matrix itself is NOT reused - it is specific to the STL
      // it belongs to and would tip a different model on its end.
      oldPart.updateMatrixWorld(true);
      const slotMatrix = oldPart.matrix.clone();
      const slotBox = new THREE.Box3().setFromObject(oldPart);
      // Cancelling the replacement must also undo any move made before the Replace click.
      const oldPartRestoreMatrix = (editOriginalMatrixRef.current ?? slotMatrix).clone();
      // Contacts the replaced part already had are inherent to the plan; any other overlap is the replacement's own.
      const slotContactIds = new Set(
        findIntersectingParts(oldPart, createCollisionContext(oldPart, collectCollisionCandidates(oldPart))).map((obj) => obj.uuid)
      );
      const strictSlotContactIds = findIntersectingParts(
        oldPart,
        createCollisionContext(oldPart, collectCollisionCandidates(oldPart).filter((obj) => !isSupportPartId(obj.uuid)), REPLACEMENT_CLEARANCE_FT),
      ).map((obj) => obj.uuid);

      // From here on the replace flow owns scene visibility; the effect below must not re-init it
      // while we await the replacement model.
      replacementAcceptedRef.current = true;

      if (isEditMode) {
        exitEditMode(false);
      }

      let replacementObj: THREE.Object3D | null = null;
      let replacementWasPlaced = false;
      let replacementOriginalMatrix = slotMatrix.clone();
      let replacementOriginalVisible = false;

      if (replacement.isAlreadyPlaced) {
        const placedObj = scenePartsMap[replacement.partId] || getPartFromScene(replacement.partId);
        if (placedObj) {
          replacementObj = placedObj;
          replacementWasPlaced = true;
          replacementOriginalMatrix = placedObj.matrix.clone();
          replacementOriginalVisible = placedObj.visible;
        }
      } else {
        // A previous attempt may have already loaded this model; reloading would add a duplicate
        // object carrying the same uuid.
        const loaded =
          scenePartsMap[replacement.partId] ||
          getPartFromScene(replacement.partId) ||
          (await loadSinglePart({
            part_id: replacement.partId,
            transform_matrix: fromMatrix4(new THREE.Matrix4()),
            sasKeys: [replacement.name, replacement.mark, replacement.globalId],
          }));
        if (loaded) {
          replacementObj = loaded;
          replacementOriginalMatrix = loaded.matrix.clone();
          setScenePartsMap((prev) => ({ ...prev, [replacement.partId]: loaded }));
        }
      }

      if (!replacementObj) {
        // Nothing was swapped: stay in edit mode on the original part.
        currentPartRef.current = oldPart;
        setActivePartId(oldPart.uuid);
        oldPart.visible = true;
        setPartColor(oldPart, EDIT_ACTIVE_COLOR);
        handleEnterEditMode();
        enqueueSnackbar(`Could not find 3D model for ${replacement.name}`, { variant: 'error' });
        return;
      }

      // An already nested part keeps the orientation the algorithm gave it; a never-nested one has
      // no valid pose yet, so its axes are matched to the slot to stop it standing on end.
      const placement = computeSlotPlacementMatrix(
        replacementObj,
        replacementOriginalMatrix,
        slotBox,
        !replacementWasPlaced,
      );

      // Centring on the slot only accounts for the replaced part's volume, so a larger part would
      // hang through the trailer walls or roof; clamp it back inside where possible rather than
      // rejecting the replacement outright.
      const fittedPlacement = fitPlacementToTruckBounds(
        replacementObj,
        placement,
        getTruckBoundsForEdit(),
      );

      replacementObj.matrixAutoUpdate = false;
      replacementObj.matrix.copy(fittedPlacement);
      replacementObj.matrixWorldNeedsUpdate = true;
      replacementObj.visible = true;
      replacementObj.updateMatrixWorld(true);

      oldPart.visible = false;
      oldPart.updateMatrixWorld(true);

      currentPartRef.current = replacementObj;
      setActivePartId(replacementObj.uuid);
      setPartColor(replacementObj, EDIT_ACTIVE_COLOR);

      // Entering edit mode never clears this (exiting does), and the positions request it starts
      // must already know which part is being replaced.
      replacedSlotInfoRef.current = {
        oldPart,
        oldPartId: oldPart.uuid,
        oldPartMatrix: oldPartRestoreMatrix,
        replacementPartId: replacement.partId,
        replacementWasPlaced,
        replacementOriginalMatrix,
        replacementOriginalVisible,
      };

      handleEnterEditMode();

      // handleEnterEditMode whitelisted whatever the replacement already touches; keep only the slot's own contacts.
      const replacementContext = editCollisionContextRef.current;
      if (replacementContext) {
        slotContactIds.delete(replacementObj.uuid);
        editBaselineContactsRef.current = slotContactIds;
        const strictCandidates = collectCollisionCandidates(replacementObj).filter((obj) => !isSupportPartId(obj.uuid));
        editStrictContextRef.current = createCollisionContext(replacementObj, strictCandidates, REPLACEMENT_CLEARANCE_FT);
        editStrictBaselineRef.current = new Set(strictSlotContactIds);
        editStrictBaselineRef.current.delete(replacementObj.uuid);
        updatePlacementValidity();
      }

      enqueueSnackbar(`Selected "${replacement.name}". You can now position it.`, {
        variant: 'success',
      });
    },
    [
      viewer,
      isEditMode,
      exitEditMode,
      scenePartsMap,
      setScenePartsMap,
      getPartFromScene,
      loadSinglePart,
      setPartColor,
      handleEnterEditMode,
      collectCollisionCandidates,
      updatePlacementValidity,
      getTruckBoundsForEdit,
      currentPartRef,
      setActivePartId,
      replacementAcceptedRef,
    ]
  );

  // Cancel editing with Escape.
  useEffect(() => {
    if (!isEditMode) return;

    const onKeyDown = (event: KeyboardEvent) => {
      // Let the replace dialog consume Escape instead of cancelling the whole edit.
      if (event.key === 'Escape' && !isReplaceModalOpen) {
        cancelOrientationAnimation(false);
        exitEditMode(true);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isEditMode, isReplaceModalOpen, cancelOrientationAnimation, exitEditMode]);

  // Clean up any leftover gizmo/proxy if the viewer is destroyed mid-edit.
  useEffect(() => {
    return () => {
      const transformControls = viewer ? (viewer as any).transformControls : null;

      if (transformControls && editDragListenerRef.current) {
        transformControls.removeEventListener('objectChange', editDragListenerRef.current);
      }
      editDragListenerRef.current = null;

      if (transformControls && editDragEndListenerRef.current) {
        transformControls.removeEventListener('dragging-changed', editDragEndListenerRef.current);
      }
      editDragEndListenerRef.current = null;

      if (transformControls) {
        transformControls.getSelectedObjects = () => [];
      }
      transformControls?.detach?.();

      if (orientationAnimFrameRef.current !== null) {
        cancelAnimationFrame(orientationAnimFrameRef.current);
        orientationAnimFrameRef.current = null;
      }
      isOrientationAnimatingRef.current = false;
      targetOrientationStepRef.current = null;

      editPivotRef.current?.parent?.remove(editPivotRef.current);
      editPivotRef.current = null;
      editPivotStartRef.current = null;
      editBasePositionRef.current = null;
      editRotationBaseMatrixRef.current = null;
      editClampedDeltaRef.current = null;
      editChildrenRelativeMatricesRef.current = null;
      editChildrenOriginalMatricesRef.current = null;
      editAvailableOrientationsRef.current = null;
      editBaseFloorZRef.current = null;
      editPartIndexRef.current = undefined;
      discardPartPositions();
      editIsDraggingRef.current = false;
    };
  }, [viewer, discardPartPositions]);

  return {
    isEditMode,
    isPartIntersecting,
    isOrientationUnfit,
    isLoadingPartPositions,
    editOrientationAngle,
    editOrientationStep,
    editOrientationTotal,
    hasEngineOrientations,
    editPartRef,
    editIsIntersectingRef,
    editCurrentOrientationIndexRef,
    editGridRef,
    replacedSlotInfoRef,
    handleOrientationNext,
    handleOrientationPrev,
    handleEnterEditMode,
    exitEditMode,
    cancelOrientationAnimation,
    handleConfirmReplace,
  };
}
